/**
 * TemplateCard — library grid card, S3 card anatomy (DESIGN-PROPOSAL §4.2
 * verbatim + CONSULTS §1/§4).
 *
 * Anatomy: Shape strip (S2 strip variant, same model at thumbnail scale) +
 * name (semibold, largest) + 1-line description + spec line (tracked caps,
 * tabular numerals, floor-clamped varying-fields-only per A3) + YOURS chip
 * (only badge on the surface — the unmarked Standard state is the common
 * state) + always-visible Use (coral, sole primary emphasis) + always-
 * visible overflow (affordances are never hover-gated).
 *
 * The raised superscript floor tick is the SOLE in-line marking on the spec
 * line (CONSULTS §4); its color only seconds the tick, never carries it.
 *
 * Presentation seam: props-only + pure derivations (defaultConfigToRecipe /
 * parseShapeFloor / buildSpecLine — no data/app state, no service imports).
 * Noun-agnostic: all user-facing strings via GALLERY_STRINGS.
 */

import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Rocket, MoreHorizontal, Copy, Trash2, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BlogTemplateSummary } from '@/services/blogTemplatesService';
import { defaultConfigToRecipe } from './recipePayload';
import { parseShapeFloor } from './shape/shapeFloor';
import { RecipeShape } from './shape/RecipeShape';
import { buildSpecLine } from './shape/specLine';
import { GALLERY_STRINGS } from './galleryStrings';

export interface TemplateCardProps {
  template: BlogTemplateSummary;
  isSelected: boolean;
  onSelect: (template: BlogTemplateSummary) => void;
  onUse: (template: BlogTemplateSummary) => void;
  /** W2 — opens the TemplatePreviewDrawer for this template (overflow only;
   *  the card surface gains NO new always-visible control). Optional so the
   *  card stays drop-in for surfaces without a drawer host. */
  onExpandShape?: (template: BlogTemplateSummary) => void;
  /** Optional: the Customize menu item is omitted when no handler is given, so
   *  a surface that doesn't offer customization renders no dead control. */
  onCustomize?: (template: BlogTemplateSummary) => void;
  /** Optional: the Delete menu item requires BOTH this handler and canDelete;
   *  a client surface passes neither and the item never renders. */
  onDelete?: (template: BlogTemplateSummary) => void;
  /** Delete affordance gate — computed by the caller (presentation seam;
   *  the card never imports the service layer). */
  canDelete: boolean;
}

export const TemplateCard = React.memo<TemplateCardProps>(({ template, isSelected, onSelect, onUse, onExpandShape, onCustomize, onDelete, canDelete }) => {
  // Pure display derivations from props (presentation seam; no state).
  const recipe = useMemo(
    () => defaultConfigToRecipe(template.default_config),
    [template.default_config],
  );
  const floor = useMemo(
    () => parseShapeFloor(template.shape_floor),
    [template.shape_floor],
  );
  const specSegments = useMemo(() => buildSpecLine(recipe, floor), [recipe, floor]);

  return (
    <Card
      data-testid="template-card"
      className={cn(
        "group cursor-pointer transition-all duration-200 relative",
        isSelected
          ? "border-primary border-2 shadow-md bg-primary/5"
          : "hover:border-primary/50 hover:shadow-md"
      )}
      onClick={() => onSelect(template)}
    >
      {/* YOURS chip — the ONLY badge on the surface (teal-dark on teal-light);
          Standard cards carry no chip (the unmarked state is the common state). */}
      {!template.is_system && (
        <div className="absolute top-2 left-2 z-10">
          <Badge
            variant="secondary"
            className="bg-teal-light text-teal-dark hover:bg-teal-light text-[10px] px-1.5 py-0"
          >
            {GALLERY_STRINGS.card.yours}
          </Badge>
        </div>
      )}

      {/* Overflow menu — ALWAYS visible (affordances are never hover-gated) */}
      <div
        className="absolute top-2 right-2 z-10 flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="More actions"
            className="h-7 w-7 rounded-md hover:bg-accent inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <MoreHorizontal className="h-4 w-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onUse(template)}>
              <Rocket className="h-4 w-4 mr-2" />
              {GALLERY_STRINGS.card.useTemplate}
            </DropdownMenuItem>
            {onExpandShape && (
              <DropdownMenuItem onClick={() => onExpandShape(template)}>
                <ChevronRight className="h-4 w-4 mr-2" />
                {GALLERY_STRINGS.card.expandShape}
              </DropdownMenuItem>
            )}
            {onCustomize && (
              <DropdownMenuItem onClick={() => onCustomize(template)}>
                <Copy className="h-4 w-4 mr-2" />
                {GALLERY_STRINGS.card.customizeCopy}
              </DropdownMenuItem>
            )}
            {canDelete && onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(template)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {GALLERY_STRINGS.card.delete}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CardHeader className="pb-3 pt-8">
        <div className="flex gap-3">
          {/* Shape strip — the S2 skeleton at thumbnail scale (same model) */}
          <RecipeShape
            recipe={recipe}
            floor={floor}
            variant="strip"
            className="w-10 flex-shrink-0 pt-1"
          />
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base font-semibold">{template.name}</CardTitle>
            <CardDescription className="text-sm line-clamp-1">
              {template.description || GALLERY_STRINGS.card.noDescription}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Spec line — the scent. Tracked caps + tabular numerals; varying
            fields only (A3); floor-clamped ranges (A1); the raised
            superscript tick is the SOLE in-line marking (CONSULTS §4). */}
        <p
          data-testid="spec-line"
          className="mb-3 text-xs uppercase tracking-wide tabular-nums text-muted-foreground"
        >
          {specSegments.map((segment, i) => (
            <React.Fragment key={segment.key}>
              {i > 0 && <span aria-hidden="true"> · </span>}
              <span data-spec-segment={segment.key} data-floored={segment.floored}>
                {segment.text}
                {segment.floored && (
                  <sup
                    data-floor-tick="true"
                    title={segment.floorNote}
                    className="text-coral-dark"
                  >
                    *
                  </sup>
                )}
              </span>
            </React.Fragment>
          ))}
        </p>

        {/* Quick actions — always visible; Use keeps coral (sole primary) */}
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            className="flex-1 bg-coral hover:bg-coral-hover"
            onClick={() => onUse(template)}
          >
            {GALLERY_STRINGS.card.use}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
});

TemplateCard.displayName = 'TemplateCard';
